import React from 'react';

/**
 * Progress Bar Component for Cart Checkout Journey
 * Shows 5 steps: Select Items → Payment Method → Payment → Shipping → Confirm
 */
const CartProgressBar = ({ currentStep = 1, onStepClick = null }) => {
  const steps = [
    {
      id: 1,
      title: 'เลือกสินค้า',
      subtitle: 'Select Items',
      icon: '🛒'
    },
    {
      id: 2,
      title: 'ชำระเงิน',
      subtitle: 'Payment Method',
      icon: '💳'
    },
    {
      id: 3,
      title: 'แนบหลักฐาน',
      subtitle: 'Upload Proof',
      icon: '📎'
    },
    {
      id: 4,
      title: 'จัดส่ง',
      subtitle: 'Shipping',
      icon: '🚚'
    },
    {
      id: 5,
      title: 'ยืนยัน',
      subtitle: 'Confirm',
      icon: '✅'
    }
  ];

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'pending';
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500 text-white border-green-500';
      case 'current':
        return 'bg-blue-500 text-white border-blue-500 ring-2 ring-blue-300';
      case 'pending':
        return 'bg-gray-200 text-gray-500 border-gray-300';
      default:
        return 'bg-gray-200 text-gray-500 border-gray-300';
    }
  };

  const progress = ((currentStep - 1) / (steps.length - 1)) * 100;

  return (
    <div className="w-full mb-6">
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            ความคืบหน้า
          </span>
          <span className="text-sm font-medium text-blue-600">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-green-500 via-blue-500 to-green-500 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Steps - Desktop */}
      <div className="hidden md:block relative">
        <div className="flex justify-between items-start">
          {steps.map((step, index) => {
            const status = getStepStatus(step.id);
            const isLast = index === steps.length - 1;
            const isClickable = onStepClick && (status === 'completed' || status === 'current');

            return (
              <div 
                key={step.id} 
                className="flex flex-col items-center relative flex-1"
                style={{ maxWidth: `${100 / steps.length}%` }}
              >
                {/* Connector Line */}
                {!isLast && (
                  <div className="absolute top-6 left-[60%] right-0 h-0.5 -z-0">
                    <div 
                      className={`h-full transition-all duration-500 ${
                        status === 'completed' ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                      style={{ 
                        width: status === 'completed' ? '100%' : '0%'
                      }}
                    ></div>
                  </div>
                )}

                {/* Step Circle */}
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick(step.id)}
                  disabled={!isClickable}
                  className={`
                    w-12 h-12 rounded-full border-2 flex items-center justify-center
                    transition-all duration-300 relative z-10
                    ${getStepColor(status)}
                    ${status === 'current' ? 'shadow-lg scale-110' : ''}
                    ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-default'}
                    disabled:opacity-50
                  `}
                >
                  <span className="text-lg">
                    {status === 'completed' ? '✓' : step.icon}
                  </span>
                </button>

                {/* Step Label */}
                <div className="mt-3 text-center w-full">
                  <div className={`
                    text-xs font-semibold
                    ${status === 'current' ? 'text-blue-600' : 
                      status === 'completed' ? 'text-green-600' : 'text-gray-500'}
                  `}>
                    {step.title}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {step.subtitle}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Steps - Mobile */}
      <div className="md:hidden">
        <div className="space-y-2">
          {steps.map((step) => {
            const status = getStepStatus(step.id);
            const isClickable = onStepClick && (status === 'completed' || status === 'current');
            
            return (
              <button
                key={step.id}
                type="button"
                onClick={() => isClickable && onStepClick(step.id)}
                disabled={!isClickable}
                className={`
                  w-full flex items-center p-3 rounded-lg border-2 transition-all duration-300
                  ${status === 'completed' ? 'bg-green-50 border-green-200' :
                    status === 'current' ? 'bg-blue-50 border-blue-300' :
                    'bg-gray-50 border-gray-200'}
                  ${isClickable ? 'hover:shadow-md' : ''}
                  disabled:opacity-50
                `}
              >
                <div 
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0
                    ${getStepColor(status)}
                  `}
                >
                  <span className="text-base">
                    {status === 'completed' ? '✓' : step.icon}
                  </span>
                </div>
                <div className="flex-1 text-left">
                  <div className={`
                    font-semibold text-sm
                    ${status === 'current' ? 'text-blue-600' : 
                      status === 'completed' ? 'text-green-600' : 'text-gray-500'}
                  `}>
                    {step.title}
                  </div>
                  <div className="text-xs text-gray-400">
                    {step.subtitle}
                  </div>
                </div>
                {status === 'current' && (
                  <div className="text-blue-500 animate-pulse ml-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CartProgressBar;










